import React, { useState, useCallback, useEffect, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Link } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import SVG from 'components/icons/SVG';
// import MarketId from 'components/shared/MarketId';
import Heading from 'components/shared/Heading';
import MarketsPopover from 'components/trade/MarketsPopover';
import { comparePropsIgnoringFunctions } from 'helpers/memo';
import styles from './styles.module.scss';

const microapps = window.MICROFRONTENDS_CONFIG;

const tabWidth = 215; // If you change this, update value in MarketTabs/styles.module.scss;

function MarketTabs(props) {
  const [pinnedMarkets, setPinnedMarkets] = useState([props.pair]);
  const [marketSelectorOpen, setMarketSelectorOpen] = useState(false);
  const [addButtonEnabled, setAddButtonEnabled] = useState(true);
  const popoverRef = useRef(null);

  const updateAddButtonEnabled = useCallback(() => {
    const pins = pinnedMarkets;

    if (window.innerWidth - pins.length * tabWidth < 500) {
      setAddButtonEnabled(false);
    } else setAddButtonEnabled(true);
  }, [pinnedMarkets]);

  const closePopoverOnScroll = useCallback(
    e => {
      if (marketSelectorOpen && !popoverRef.current?.contains(e.target)) {
        setMarketSelectorOpen(false);
      }
    },
    [marketSelectorOpen]
  );

  useEffect(() => {
    updateAddButtonEnabled();
    window.addEventListener('scroll', updateAddButtonEnabled, { passive: true });
    window.addEventListener('resize', updateAddButtonEnabled, { passive: true });
    window.addEventListener('scroll', closePopoverOnScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', updateAddButtonEnabled);
      window.removeEventListener('resize', updateAddButtonEnabled);
      window.removeEventListener('scroll', closePopoverOnScroll);
    };
  }, [marketSelectorOpen, updateAddButtonEnabled, closePopoverOnScroll]);

  const addToPinnedMarkets = newMarket => {
    const newPins = [...pinnedMarkets, newMarket.pair];
    setPinnedMarkets(newPins);
    // updateAddButtonEnabled(newPins);
  };

  const removePinnedMarket = marketPair => {
    const newPins = pinnedMarkets.filter(pinnedMarketPair => pinnedMarketPair !== marketPair);
    setPinnedMarkets(newPins);
    // updateAddButtonEnabled(newPins);
    if (marketPair === props.pair) {
      window.HISTORY.push(`${microapps.routes.trading.path.split(':')[0]}${newPins[0]}`);
    }
  };

  const hideAddTabButton = pinnedMarkets.length === props.markets.data.numberOfMarkets || !addButtonEnabled;

  return (
    <div className={styles.marketTabs}>
      <div className={styles.pinnedMarketsRow}>
        <TransitionGroup component={React.Fragment}>
          {pinnedMarkets.map((marketPair, index) => {
            const assets = marketPair.split('-');
            const active = marketPair === props.pair;
            return (
              <CSSTransition key={marketPair} timeout={3000} classNames="pinned-market">
                <div
                  className={classnames(styles.marketLinkWrapper, {
                    'pinned-market-enter-done': index === 0
                  })}
                  key={marketPair}>
                  <Link
                    to={`${microapps.routes.trading.path.split(':')[0]}${marketPair}`}
                    className={styles.marketLink}>
                    <nbx-asset-icon
                      size="26"
                      asset={assets[0]}
                      muted={!active}
                      key={`${marketPair}-${active}`}
                    />
                    <Heading tag="h3" styles={[{ [styles.inactive]: !active }]}>
                      <span>{assets[0]}</span> <span>/ {assets[1]}</span>
                    </Heading>
                  </Link>
                  <button
                    className={classnames([
                      styles.closeTabButton,
                      { [styles.hiddenButton]: pinnedMarkets.length === 1 }
                    ])}
                    type="button"
                    onClick={() => removePinnedMarket(marketPair)}>
                    <SVG icon="cross" muted />
                  </button>
                </div>
              </CSSTransition>
            );
          })}
        </TransitionGroup>
      </div>
      <span className={styles.addTabButtonWrapper}>
        <button
          className={classnames([
            styles.addTabButton,
            {
              [styles.addTabPopoverOpen]: marketSelectorOpen,
              [styles.hiddenButton]: hideAddTabButton
            }
          ])}
          id="add-tab-button"
          type="button"
          onClick={() => setMarketSelectorOpen(!marketSelectorOpen)}>
          <SVG icon="plus2" />
        </button>
        <MarketsPopover
          isVisible={marketSelectorOpen}
          preferences={props.preferences}
          pinnedMarkets={pinnedMarkets}
          closePopover={() => {
            setMarketSelectorOpen(false);
          }}
          onClickCallback={addToPinnedMarkets}
          popoverRef={popoverRef}
        />
      </span>
    </div>
  );
}

MarketTabs.propTypes = {
  pair: PropTypes.string.isRequired,
  markets: PropTypes.object.isRequired,
  preferences: PropTypes.object.isRequired
};

export default memo(MarketTabs, comparePropsIgnoringFunctions);
